import type { CSSProperties } from "react"
import { FormEventHandler } from "react"
import { Link, router, useForm } from "@inertiajs/react"
import { AppLayout } from "../../components/AppLayout"
import { Badge } from "../../components/Badge"
import { CardEditorNav } from "../../components/CardEditorNav"
import { ColorPicker } from "../../components/ColorPicker"
import { Field } from "../../components/Field"
import { ImageCropEditor } from "../../components/ImageCropEditor"
import { IconBadge } from "../../components/IconBadge"
import { SectionCard } from "../../components/SectionCard"
import { SocialIconBadge } from "../../components/SocialIconBadge"
import { TextArea } from "../../components/TextArea"
import { TextInput } from "../../components/TextInput"
import { buttonIconOptions } from "../../utils/buttonIcons"
import { categoryOptions, getCategoryLabel } from "../../utils/categories"
import { contrastingTextColor, hexToRgb, normalizeHexColor } from "../../utils/color"
import { FiCheckCircle, FiExternalLink, FiEye, FiEyeOff } from "react-icons/fi"
import { getSocialPlatformColor, socialPlatformInputHint, socialPlatformInputLabel, socialPlatformInputPlaceholder, socialPlatformOptions } from "../../utils/socialPlatforms"
import { cardTemplates } from "../../utils/cardTemplates"

type SocialLink = { platform: string; handle: string; url: string; icon_color: string; position: number }
type ButtonLink = { label: string; url: string; icon_name: string | null; button_color: string; position: number }
type FaqItem = { question: string; answer: string; position: number }

type CardData = {
  id: number
  user_id: number
  slug: string
  title: string
  subtitle: string | null
  description: string | null
  phone: string | null
  whatsapp: string | null
  whatsapp_message: string | null
  email: string | null
  website_url: string | null
  address: string | null
  maps_url: string | null
  is_published: boolean
  theme: string
  primary_color: string
  logo_radius: number
  logo_focus_x: number
  logo_focus_y: number
  cover_focus_x: number
  cover_focus_y: number
  show_contact_button: boolean
  show_vcf_button: boolean
  show_feedback_button: boolean
  opening_hours_hidden: boolean
  content_sections_order: string[]
  hidden_sections: string[]
  section_titles: Record<string, string>
  hidden_section_titles: string[]
  categories: string[]
  faqs: FaqItem[]
  testimonials: Array<{ id: number; name: string; role: string | null; message: string; rating: number }>
  public_url: string
  preview_url: string
  logo_url: string | null
  cover_image_url: string | null
  social_links: SocialLink[]
  buttons: ButtonLink[]
}

type UserOption = {
  id: number
  name: string
  username: string
  email: string
  admin: boolean
}

type Props = {
  card: CardData
  users: UserOption[]
  errors: Record<string, string>
  section: string
}

export default function EditCard({ card, users, section }: Props) {
  const customerMode = window.location.pathname.startsWith("/dash/")
  const analyticsUrl = customerMode ? `/dash/card/${card.id}/analytics` : `/admin/cards/${card.id}/analytics`
  const previewUrl = customerMode ? `/dash/card/${card.id}/preview` : card.preview_url
  const editUrl = customerMode ? `/dash/card/${card.id}` : `/admin/cards/${card.id}`
  const publishUrl = customerMode ? `/dash/card/${card.id}/publish` : `/admin/cards/${card.id}/publish`
  const unpublishUrl = customerMode ? `/dash/card/${card.id}/unpublish` : `/admin/cards/${card.id}/unpublish`
  const buildSectionState = (
    hiddenSections: string[],
    hiddenSectionTitles: string[],
    sectionTitles: Record<string, string>,
    contentSectionsOrder: string[],
  ) =>
    JSON.stringify({
      visibility: Object.fromEntries(contentSectionsOrder.map((section) => [section, !hiddenSections.includes(section)])),
      title_visibility: Object.fromEntries(contentSectionsOrder.map((section) => [section, !hiddenSectionTitles.includes(section)])),
      section_titles: sectionTitles,
    })

  const form = useForm({
    title: card.title,
    slug: card.slug,
    subtitle: card.subtitle ?? "",
    description: card.description ?? "",
    phone: card.phone ?? "",
    whatsapp: card.whatsapp ?? "",
    whatsapp_message: card.whatsapp_message ?? "",
    email: card.email ?? "",
    website_url: card.website_url ?? "",
    address: card.address ?? "",
    maps_url: card.maps_url ?? "",
    is_published: card.is_published,
    theme: card.theme,
    primary_color: card.primary_color,
    logo_radius: card.logo_radius ?? 24,
    logo_focus_x: card.logo_focus_x,
    logo_focus_y: card.logo_focus_y,
    cover_focus_x: card.cover_focus_x,
    cover_focus_y: card.cover_focus_y,
    show_contact_button: card.show_contact_button ?? true,
    show_vcf_button: card.show_vcf_button ?? true,
    show_feedback_button: card.show_feedback_button ?? true,
    user_id: card.user_id,
    opening_hours_hidden: card.opening_hours_hidden,
    content_sections_order: card.content_sections_order.length > 0 ? card.content_sections_order : ["hero", "info", "buttons", "social_links", "opening_hours", "faq", "testimonials", "gallery"],
    hidden_sections: card.hidden_sections,
    section_titles: card.section_titles,
    hidden_section_titles: card.hidden_section_titles,
    section_state: buildSectionState(
      card.hidden_sections,
      card.hidden_section_titles,
      card.section_titles,
      card.content_sections_order.length > 0 ? card.content_sections_order : ["hero", "info", "buttons", "social_links", "opening_hours", "faq", "testimonials", "gallery"],
    ),
    categories: card.categories,
    faqs: card.faqs.length > 0 ? card.faqs : [{ question: "", answer: "", position: 0 }],
    social_links:
      card.social_links.length > 0
        ? card.social_links
        : [{ platform: "instagram", handle: "", url: "", icon_color: getSocialPlatformColor("instagram"), position: 0 }],
    buttons: card.buttons.length > 0 ? card.buttons : [{ label: "", url: "", icon_name: null, button_color: card.primary_color, position: 0 }],
    logo: null as File | null,
    cover_image: null as File | null,
  })
  const { data, setData, patch, processing, errors, transform } = form

  const submit: FormEventHandler = (event) => {
    event.preventDefault()
    transform((current) => ({
      ...current,
      categories_state: JSON.stringify(current.categories),
      faqs_state: JSON.stringify(current.faqs),
      editor_section: currentSection,
    }))
    patch(
      editUrl,
      {
        forceFormData: true,
        preserveScroll: true,
      },
    )
  }

  const updateSocialLink = (index: number, field: keyof SocialLink, value: string) => {
    const next = [...data.social_links]
    next[index] = { ...next[index], [field]: field === "position" ? Number(value) : value } as SocialLink
    setData("social_links", next)
  }


  const updateButton = (index: number, field: keyof ButtonLink, value: string) => {
    const next = [...data.buttons]
    next[index] = {
      ...next[index],
      [field]: field === "position" ? Number(value) : field === "icon_name" ? (value || null) : value,
    } as ButtonLink
    setData("buttons", next)
  }

  const updatePrimaryColor = (value: string) => {
    const color = normalizeHexColor(value)
    const previousColor = normalizeHexColor(data.primary_color)
    setData("primary_color", color)
    setData("buttons", data.buttons.map((button) => normalizeHexColor(button.button_color || previousColor) === previousColor ? { ...button, button_color: color } : button))
  }

  const updateFaq = (index: number, field: keyof FaqItem, value: string) => {
    const next = [...data.faqs]
    next[index] = {
      ...next[index],
      [field]: field === "position" ? Number(value) : value,
    } as FaqItem
    setData("faqs", next)
  }

  const sectionLabels: Record<string, string> = {
    hero: "כותרת עליונה",
    info: "פרטי קשר",
    buttons: "כפתורים",
    social_links: "קישורים חברתיים",
    opening_hours: "שעות פתיחה",
    faq: "שאלות ותשובות",
    testimonials: "המלצות",
    gallery: "גלריה",
  }

  const moveSection = (sectionName: string, direction: -1 | 1) => {
    const editableSections = data.content_sections_order.filter((section) => !["opening_hours", "info"].includes(section))
    const index = editableSections.indexOf(sectionName)
    const target = index + direction
    if (index < 0 || target < 0 || target >= editableSections.length) return
    if (editableSections[index] === "hero" || editableSections[target] === "hero") return
    const [section] = editableSections.splice(index, 1)
    editableSections.splice(target, 0, section)
    const fixedSections = data.content_sections_order.filter((section) => ["opening_hours", "info"].includes(section))
    setData("content_sections_order", [...editableSections, ...fixedSections])
  }

  const toggleSection = (section: string) => {
    const next = data.hidden_sections.includes(section)
      ? data.hidden_sections.filter((current) => current !== section)
      : [...data.hidden_sections, section]
    setData("hidden_sections", next)
    setData("section_state", buildSectionState(next, data.hidden_section_titles, data.section_titles, data.content_sections_order))
  }

  const updateSectionTitle = (section: string, value: string) => {
    const next = {
      ...data.section_titles,
      [section]: value,
    }
    setData("section_titles", next)
    setData("section_state", buildSectionState(data.hidden_sections, data.hidden_section_titles, next, data.content_sections_order))
  }

  const toggleSectionTitle = (section: string) => {
    const next = data.hidden_section_titles.includes(section)
      ? data.hidden_section_titles.filter((current) => current !== section)
      : [...data.hidden_section_titles, section]
    setData("hidden_section_titles", next)
    setData("section_state", buildSectionState(data.hidden_sections, next, data.section_titles, data.content_sections_order))
  }

  const toggleCategory = (category: string) => {
    const next = data.categories.includes(category)
      ? data.categories.filter((current) => current !== category)
      : [...data.categories, category]
    setData("categories", next)
  }

  const swatches = ["#0f766e", "#2563eb", "#7c3aed", "#db2777", "#ea580c", "#15803d", "#111827"]
  const previewColor = normalizeHexColor(data.primary_color)
  const { r, g, b } = hexToRgb(previewColor)
  const previewText = contrastingTextColor(previewColor)
  const fullCardUrl = (() => {
    try {
      return new URL(card.public_url, window.location.origin).toString()
    } catch {
      return `${window.location.origin}/${data.slug}`
    }
  })()
  const previewStyle = {
    background: `linear-gradient(135deg, ${previewColor} 0%, rgba(${r} ${g} ${b} / 0.72) 100%)`,
    color: previewText,
  } as CSSProperties
  const editorSections = ["overview", "contact", "social", "buttons", "design", "structure", "faq", "media", "gallery", "engagement"]
  const currentSection = editorSections.includes(section) ? section : "overview"
  const editableSectionOrder = data.content_sections_order.filter((sectionName) => !["opening_hours", "info"].includes(sectionName))

  return (
    <AppLayout title={`${card.title} | ${customerMode ? "הכרטיס שלי" : "עריכת כרטיס"}`} customerMode={customerMode}>
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <h1 className="text-3xl font-bold">{customerMode ? "הכרטיס שלי" : "עריכת כרטיס"}</h1>
            <Badge tone={data.is_published ? "green" : "amber"}>{data.is_published ? "מופיע לכולם" : "טיוטה"}</Badge>
          </div>
          <p className="text-slate-600">/{card.slug}</p>
        </div>
      </div>

      <CardEditorNav
        cardId={card.id}
        customerMode={customerMode}
        activeSection={currentSection}
        analyticsHref={analyticsUrl}
        actions={[
          { key: "preview", label: "תצוגה", href: previewUrl, icon: <FiEye aria-hidden="true" /> },
          { key: "public", label: "פתיחת הכרטיס", href: card.public_url, external: true, icon: <FiExternalLink aria-hidden="true" /> },
          data.is_published
            ? { key: "unpublish", label: "הסתר מהציבור", href: unpublishUrl, method: "patch" as const, icon: <FiEyeOff aria-hidden="true" /> }
            : { key: "publish", label: "הצג לכולם", href: publishUrl, method: "patch" as const, icon: <FiCheckCircle aria-hidden="true" /> },
        ]}
      />

      <form onSubmit={submit} className="space-y-6">
        {currentSection === "overview" ? (
          <>
            <SectionCard title="פרטי העסק" description="שם, כתובת ופרטים בסיסיים">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="כותרת" error={errors.title}>
                  <TextInput value={data.title} onChange={(e) => setData("title", e.target.value)} />
                </Field>
                <Field label="כתובת הכרטיס" error={errors.slug} hint="הכתובת שאפשר לשתף עם לקוחות">
                  <TextInput dir="ltr" value={data.slug} onChange={(e) => setData("slug", e.target.value)} />
                  <div className="mt-2 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-500" dir="ltr">
                    {fullCardUrl}
                  </div>
                </Field>
                <Field label="כותרת משנה" error={errors.subtitle}>
                  <TextInput value={data.subtitle} onChange={(e) => setData("subtitle", e.target.value)} />
                </Field>
              </div>
              <div className="mt-4">
                <Field label="תיאור" error={errors.description}>
                  <TextArea value={data.description} onChange={(e) => setData("description", e.target.value)} />
                </Field>
              </div>
            </SectionCard>

            <SectionCard title="בעלות" description="שייך את הכרטיס למשתמש">
              <Field label="משתמש">
                <select className="input-base" value={data.user_id} onChange={(e) => setData("user_id", Number(e.target.value))}>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} / @{user.username}
                    </option>
                  ))}
                </select>
              </Field>
            </SectionCard>

            <SectionCard title="קטגוריות" description="למשל מסעדה, קליניקה או חנות. יעזור לנו לבנות אינדקס בעתיד">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {categoryOptions.map((category) => (
                  <label key={category.value} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                    <input type="checkbox" checked={data.categories.includes(category.value)} onChange={() => toggleCategory(category.value)} />
                    <span className="text-sm font-medium text-slate-800">{category.label}</span>
                  </label>
                ))}
              </div>
              {data.categories.length > 0 ? <p className="mt-4 text-sm text-slate-500">נבחרו: {data.categories.map(getCategoryLabel).join(", ")}</p> : null}
            </SectionCard>

            <SectionCard title="כפתורי פעולה" description="מה יוצג בראש הכרטיס הציבורי">
              <div className="grid gap-3 md:grid-cols-3">
                <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <input type="checkbox" checked={data.show_contact_button} onChange={(e) => setData("show_contact_button", e.target.checked)} />
                  <span className="text-sm font-medium text-slate-800">צור קשר</span>
                </label>
                <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <input type="checkbox" checked={data.show_vcf_button} onChange={(e) => setData("show_vcf_button", e.target.checked)} />
                  <span className="text-sm font-medium text-slate-800">שמור אותי באנשי קשר</span>
                </label>
                <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <input type="checkbox" checked={data.show_feedback_button} onChange={(e) => setData("show_feedback_button", e.target.checked)} />
                  <span className="text-sm font-medium text-slate-800">השאירו המלצה</span>
                </label>
              </div>
            </SectionCard>

            <SectionCard title="עיצוב ופרסום" description="אפשר לשמור הכל ואז לפרסם">
              <div className="grid gap-4 md:grid-cols-[1fr_220px] md:items-start">
                <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <input type="checkbox" checked={data.is_published} onChange={(e) => setData("is_published", e.target.checked)} />
                  <span>הכרטיס פורסם</span>
                </label>

                <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-slate-50 p-3">
                  <div className="rounded-[1.5rem] p-4 shadow-sm" style={previewStyle}>
                    <div className="text-xs/5 opacity-80">תצוגה מהירה</div>
                    <div className="mt-2 text-lg font-semibold">{data.title || "שם העסק"}</div>
                    <div className="mt-3 inline-flex rounded-full bg-white/15 px-3 py-1 text-xs font-medium">
                      {normalizeHexColor(data.primary_color)}
                    </div>
                  </div>
                </div>
              </div>
            </SectionCard>
          </>
        ) : null}

        {currentSection === "design" ? (
          <>
            <SectionCard title="עיצוב הכרטיס" description="בחרו מראה וצבעים לכרטיס שהלקוחות יראו">
              <div className="grid gap-3 sm:grid-cols-2">
                {cardTemplates.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => setData("theme", template.id)}
                    className={`rounded-2xl border p-3 text-right transition ${data.theme === template.id ? "border-brand-500 bg-brand-50 ring-2 ring-brand-100" : "border-slate-200 bg-white hover:border-slate-300"}`}
                  >
                    <div className="h-20 rounded-xl" style={{ background: template.heroGradient(r, g, b) }} />
                    <div className="mt-3 text-sm font-semibold text-slate-900">{template.label}</div>
                    <div className="mt-0.5 text-xs text-slate-500">{template.description}</div>
                  </button>
                ))}
              </div>
            </SectionCard>

            <SectionCard title="צבע הכרטיס" description="בחרו צבע שיופיע בכפתורים ובכותרות">
              <div className="space-y-3">
                <ColorPicker value={data.primary_color} onChange={updatePrimaryColor} label="צבע המותג" />
                <div className="flex flex-wrap gap-2">
                  {swatches.map((color) => (
                    <button key={color} type="button" onClick={() => updatePrimaryColor(color)} className="h-10 w-10 rounded-2xl border border-white shadow-sm ring-1 ring-slate-200 transition hover:scale-105" style={{ backgroundColor: color }} aria-label={`בחר צבע ${color}`} />
                  ))}
                </div>
              </div>
            </SectionCard>
          </>
        ) : null}

        {currentSection === "contact" ? (
          <>
            <SectionCard title="פרטי קשר" description="איך הלקוחות יגיעו אליך">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="טלפון" error={errors.phone}>
                  <TextInput dir="ltr" value={data.phone} onChange={(e) => setData("phone", e.target.value)} />
                </Field>
                <Field label="מספר וואטסאפ" error={errors.whatsapp} hint="לינק וואטסאפ ייווצר אוטומטית">
                  <TextInput dir="ltr" value={data.whatsapp} onChange={(e) => setData("whatsapp", e.target.value)} />
                </Field>
                <Field label="הודעת וואטסאפ" error={errors.whatsapp_message} hint="אופציונלי, יתווסף לקישור">
                  <TextInput value={data.whatsapp_message} onChange={(e) => setData("whatsapp_message", e.target.value)} />
                </Field>
                <Field label="אימייל" error={errors.email}>
                  <TextInput type="email" value={data.email} onChange={(e) => setData("email", e.target.value)} />
                </Field>
                <Field label="אתר" error={errors.website_url}>
                  <TextInput dir="ltr" value={data.website_url} onChange={(e) => setData("website_url", e.target.value)} />
                </Field>
                <Field label="כתובת פיזית" error={errors.address}>
                  <TextInput value={data.address} onChange={(e) => setData("address", e.target.value)} />
                </Field>
                <Field label="קישור למפות" error={errors.maps_url}>
                  <TextInput dir="ltr" value={data.maps_url} onChange={(e) => setData("maps_url", e.target.value)} />
                </Field>
              </div>
            </SectionCard>

          </>
        ) : null}

        {currentSection === "social" ? (
          <>
            <SectionCard title="קישורים חברתיים" description="הקישורים שיופיעו בראש הכרטיס הציבורי">
              <div className="space-y-3">
                {data.social_links.map((link, index) => (
                  <div key={index} className="grid gap-3 md:grid-cols-[1fr_1.5fr_1fr_auto]">
                    <label className="block">
                      <span className="label-base">סוג</span>
                      <div className="flex items-center gap-3">
                        <select className="input-base" value={link.platform} onChange={(e) => updateSocialLink(index, "platform", e.target.value)}>
                          {socialPlatformOptions.map((platform) => (
                            <option key={platform.value} value={platform.value}>
                              {platform.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </label>
                    <Field label={socialPlatformInputLabel(link.platform)} hint={socialPlatformInputHint(link.platform)}>
                      <TextInput
                        placeholder={socialPlatformInputPlaceholder(link.platform)}
                        dir="ltr"
                        value={link.handle}
                        onChange={(e) => updateSocialLink(index, "handle", e.target.value)}
                      />
                    </Field>
                    <label className="block">
                      <span className="label-base">צבע</span>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={normalizeHexColor(link.icon_color || getSocialPlatformColor(link.platform))}
                          onChange={(e) => updateSocialLink(index, "icon_color", normalizeHexColor(e.target.value))}
                          className="h-12 w-14 cursor-pointer rounded-2xl border border-slate-200 bg-white p-1"
                        />
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50">
                          <SocialIconBadge platform={link.platform} backgroundColor={normalizeHexColor(link.icon_color || getSocialPlatformColor(link.platform))} />
                        </div>
                      </div>
                    </label>
                    <TextInput placeholder="סדר" type="number" value={String(link.position)} onChange={(e) => updateSocialLink(index, "position", e.target.value)} />
                  </div>
                ))}
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() =>
                    setData("social_links", [
                      ...data.social_links,
                      { platform: "instagram", handle: "", url: "", icon_color: getSocialPlatformColor("instagram"), position: data.social_links.length },
                    ])
                  }
                >
                  הוספת קישור
                </button>
              </div>
            </SectionCard>

          </>
        ) : null}

        {currentSection === "buttons" ? (
          <>
            <SectionCard title="כפתורי פעולה" description="קישורים בולטים שיופיעו בכרטיס הציבורי">
              <div className="space-y-3">
                {data.buttons.map((button, index) => (
                  <div key={index} className="grid gap-3 md:grid-cols-[1fr_1.6fr_1fr_1fr_auto]">
                    <TextInput placeholder="תווית" value={button.label} onChange={(e) => updateButton(index, "label", e.target.value)} />
                    <TextInput placeholder="כתובת" dir="ltr" value={button.url} onChange={(e) => updateButton(index, "url", e.target.value)} />
                    <label className="block">
                      <span className="label-base">אייקון</span>
                      <div className="flex items-center gap-3">
                        <select className="input-base" value={button.icon_name ?? ""} onChange={(e) => updateButton(index, "icon_name", e.target.value)}>
                          {buttonIconOptions.map((icon) => (
                            <option key={icon.value || "none"} value={icon.value}>
                              {icon.label}
                            </option>
                          ))}
                        </select>
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50">
                          <IconBadge name={button.icon_name} className="bg-brand-600 text-white" />
                        </div>
                      </div>
                    </label>
                    <label className="block">
                      <span className="label-base">צבע</span>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 shrink-0 rounded-xl border border-white shadow-sm ring-1 ring-slate-200" style={{ backgroundColor: normalizeHexColor(button.button_color || data.primary_color) }} />
                        <TextInput
                          dir="ltr"
                          value={button.button_color || data.primary_color}
                          onChange={(e) => updateButton(index, "button_color", normalizeHexColor(e.target.value))}
                          className="font-mono uppercase"
                        />
                      </div>
                    </label>
                    <TextInput placeholder="סדר" type="number" value={String(button.position)} onChange={(e) => updateButton(index, "position", e.target.value)} />
                  </div>
                ))}
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setData("buttons", [...data.buttons, { label: "", url: "", icon_name: null, button_color: data.primary_color, position: data.buttons.length }])}
                >
                  הוספת כפתור
                </button>
              </div>
            </SectionCard>
          </>
        ) : null}

        {currentSection === "structure" ? (
          <>
            <SectionCard title="סדר חלקים" description="קובע את הסדר בכרטיס הציבורי">
              <div className="space-y-3">
                {editableSectionOrder.map((sectionName, index) => (
                  <div key={sectionName} className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="font-medium text-slate-900">{sectionLabels[sectionName] ?? sectionName}</div>
                        <div className="text-sm text-slate-500">{index + 1}</div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="btn-secondary px-3 py-2 text-xs"
                          onClick={() => moveSection(sectionName, -1)}
                          disabled={index === 0 || sectionName === "hero"}
                        >
                          למעלה
                        </button>
                        <button
                          type="button"
                          className="btn-secondary px-3 py-2 text-xs"
                          onClick={() => moveSection(sectionName, 1)}
                          disabled={index === editableSectionOrder.length - 1 || sectionName === "hero"}
                        >
                          למטה
                        </button>
                      </div>
                    </div>
                    <label className="mt-3 flex items-center gap-3 rounded-2xl bg-slate-50 px-4 py-3">
                      <input type="checkbox" checked={!data.hidden_sections.includes(sectionName)} onChange={() => toggleSection(sectionName)} />
                      <span className="text-sm text-slate-700">{data.hidden_sections.includes(sectionName) ? "מוסתר" : "מוצג"}</span>
                    </label>
                    <div className="mt-3 rounded-2xl bg-slate-50 px-4 py-3">
                      <label className="flex items-center gap-3">
                        <input type="checkbox" checked={!data.hidden_section_titles.includes(sectionName)} onChange={() => toggleSectionTitle(sectionName)} />
                        <span className="text-sm text-slate-700">{data.hidden_section_titles.includes(sectionName) ? "כותרת מוסתרת" : "כותרת מוצגת"}</span>
                      </label>
                      <div className="mt-3">
                        <TextInput
                          value={data.section_titles[sectionName] ?? ""}
                          onChange={(e) => updateSectionTitle(sectionName, e.target.value)}
                          placeholder={sectionLabels[sectionName] ?? sectionName}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>


          </>
        ) : null}

        {currentSection === "faq" ? (
          <SectionCard title="שאלות נפוצות" description="שאלות ותשובות קצרות שיופיעו בכרטיס הציבורי">
            <div className="space-y-3">
              {data.faqs.map((faq, index) => (
                <div key={index} className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4">
                  <TextInput placeholder="שאלה" value={faq.question} onChange={(e) => updateFaq(index, "question", e.target.value)} />
                  <TextArea placeholder="תשובה" value={faq.answer} onChange={(e) => updateFaq(index, "answer", e.target.value)} />
                  <TextInput placeholder="סדר" type="number" value={String(faq.position)} onChange={(e) => updateFaq(index, "position", e.target.value)} />
                </div>
              ))}
              <button type="button" className="btn-secondary" onClick={() => setData("faqs", [...data.faqs, { question: "", answer: "", position: data.faqs.length }])}>
                הוספת שאלה
              </button>
            </div>
          </SectionCard>
        ) : null}

        {currentSection === "media" ? (
          <SectionCard title="תמונות" description="לוגו ותמונת כיסוי">
            <div className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm">
                  <ImageCropEditor
                    label="מוקד לוגו"
                    description="בחרו לוגו, חתכו אותו ואז שמרו"
                    existingUrl={card.logo_url}
                    value={data.logo}
                    onChange={(file) => setData("logo", file)}
                    aspect={1}
                    cropShape="round"
                    previewClassName="aspect-square"
                    previewRadius={data.logo_radius}
                  />
                  <div className="mt-4">
                    <div className="label-base">צורת הלוגו</div>
                    <div className="mt-2 grid grid-cols-4 gap-2">
                      {[
                        { value: 0, label: "חד" },
                        { value: 12, label: "עדין" },
                        { value: 24, label: "מעוגל" },
                        { value: 999, label: "עגול" },
                      ].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setData("logo_radius", option.value)}
                          className={`rounded-xl border px-2 py-2 text-xs font-medium transition ${data.logo_radius === option.value ? "border-brand-500 bg-brand-50 text-brand-700 ring-2 ring-brand-100" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-slate-500">הבחירה תופיע גם בכרטיס הציבורי.</p>
                  </div>
                </div>
                <div className="rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm">
                  <ImageCropEditor
                    label="תמונת כיסוי"
                    description="חתכו את האזור שתרצו שיופיע בראש הכרטיס"
                    existingUrl={card.cover_image_url}
                    value={data.cover_image}
                    onChange={(file) => setData("cover_image", file)}
                    aspect={16 / 9}
                    previewClassName="aspect-[16/9]"
                  />
                </div>
              </div>
            </div>
          </SectionCard>
        ) : null}

        {currentSection === "engagement" ? (
          <div id="testimonials">
            <SectionCard title="המלצות" description="אפשר לראות, לערוך ולהוסיף המלצות שנאספו מהלקוחות">
              <div className="space-y-3">
                {card.testimonials?.length ? (
                  card.testimonials.map((testimonial) => (
                    <div key={testimonial.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="font-medium text-slate-900">{testimonial.name}</div>
                          {testimonial.role ? <div className="mt-1 text-sm text-slate-500">{testimonial.role}</div> : null}
                        </div>
                        <Badge tone={testimonial.rating >= 4 ? "green" : "amber"}>{testimonial.rating}.0</Badge>
                      </div>
                      <p className="mt-3 leading-7 text-slate-700">{testimonial.message}</p>
                    </div>
                  ))
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
                    עדיין אין המלצות. אפשר להוסיף המלצה מהטופס הציבורי או ליצור אחת בהמשך.
                  </div>
                )}
              </div>
            </SectionCard>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          <button disabled={processing} className="btn-primary bg-brand-600">
            שמירה
          </button>
        </div>
      </form>
    </AppLayout>
  )
}
